// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { ApiOpportunityConfigurationResponse } from "../../../src/WorkflowsStore/Opportunity/OpportunityConfigurations/interfaces";

export const mockApiOpportunityConfigurationResponse = {
  enabledConfigs: {
    usTxAnnualReportStatus: {
      callToAction: null,
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "FEES",
          text: "Client has not demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement",
        },
        {
          key: "RESTITUTION",
          text: "Client has not maintained compliance with all restitution obligations in accordance to PD/POP-3.1.6 for the preceding two years of supervision",
        },
        {
          key: "DISCRETION",
          text: "Per the PO’s discretion, it is not in the best interest of society for the client’s reporting status to be modified to Annual Report",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Annual Report Status",
      dynamicEligibilityText:
        "client[|s] may be eligible for Annual Report Status",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "supervisionLevelIsMinimumFor3Years",
          text: "Has successfully completed three years on Low supervision",
          tooltip: "As determined by the Texas Risk Assessment System (TRAS)",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Has had no warrants issued within the preceding two years of supervision",
          tooltip:
            "This does not apply to a warrant issued in which a subsequent investigation or administrative review did not sustain the violation.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TX-annualReportStatusReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 2,
      ineligibleCriteriaCopy: [
        {
          key: "supervisionLevelIsMinimumFor3Years",
          text: "Less than one month until 3 years on Low",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Less than one month until 2 years with no sustained violation",
        },
      ],
      initialHeader:
        "Review clients who may be eligible for Annual Report Status",
      isAlert: false,
      markSubmittedOnFormDownload: false,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf",
      nonOmsCriteria: [
        {
          text: "The client has demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement, if applicable",
          tooltip:
            "Any supervision, crime victim fees and Post Secondary Education reimbursement which will continue to be due each month, unless paid in advance, while client is on Annual Report status.",
        },
        {
          text: "The client has maintained compliance with all restitution obligations for the preceding two years of supervision",
          tooltip:
            "Note: Restitution obligation does not have to be paid in full to qualify. Restitution will continue to be due each month until fully paid while client is on Annual Report status.\nIn accordance to PD/POP-3.1.6",
        },
      ],
      nonOmsCriteriaHeader: "Requirements to check",
      notifications: [
        {
          body: "ARS/ERS Policy changes went into effect on July 21, 2025. Learn what this means for you: [FAQ](https://docs.google.com/document/d/e/2PACX-1vSZ6cUATsrYOH4x72IbC3EnULM651f2taDj-rXDqVMdMrCQC_OIscuqWV0rslrlZfZeLHDQrO2af7QY/pub)",
          cta: "Acknowledge & Close",
          id: "3f78cf16-ed4f-4ea0-9c7b-903d1c5c9bf9",
          pages: ["caseload"],
          type: "info",
        },
      ],
      omsCriteriaHeader: " Requirements validated by OIMS data",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["ClientProfileDetails", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: ["usTxEarlyReleaseFromSupervision"],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TX",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "Supervision clients may be eligible for Annual Report Status if they meet certain criteria. The official policy doc can be found [here](https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf) effective Jul 1, 2025. Review clients who may be eligible below.",
      submittedTabTitle: "Submitted for Review",
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: "Eligible for Annual Report Status",
      urlSection: "AnnualReportStatus",
      zeroGrantsTooltip: null,
    },
    usTxAnnualReportStatusV2: {
      callToAction: null,
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "FEES",
          text: "Client has not demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement",
        },
        {
          key: "RESTITUTION",
          text: "Client has not maintained compliance with all restitution obligations in accordance to PD/POP-3.1.6 for the preceding two years of supervision",
        },
        {
          key: "DISCRETION",
          text: "Per the PO’s discretion, it is not in the best interest of society for the client’s reporting status to be modified to Annual Report",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Annual Report Status",
      dynamicEligibilityText:
        "client[|s] may be eligible for Annual Report Status",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "supervisionLevelIsMinimumFor3Years",
          text: "Has successfully completed three years on Low supervision",
          tooltip: "As determined by the Texas Risk Assessment System (TRAS)",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Has had no warrants issued within the preceding two years of supervision",
          tooltip:
            "This does not apply to a warrant issued in which a subsequent investigation or administrative review did not sustain the violation.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [
        {
          cellValue: null,
          columnHeader: null,
          columnId: "PERSON_NAME",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "PERSON_DISPLAY_ID",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ELIGIBILITY_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ALMOST_ELIGIBILITY_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SUPERVISION_EXPIRATION_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "STATUS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ALMOST_ELIGIBLE_STATUS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SNOOZE_ENDS_IN",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ASSIGNED_STAFF_NAME",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "INSTANCE_DETAILS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SUBMITTED_FOR",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_CURRENT_REVIEWER",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_SUBMITTED_FOR_REVIEW_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_ALL_REVIEWERS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_REVISION_REASON",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_GRANT_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "LAST_VIEWED",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "CTA_BUTTON",
          sortingFn: null,
        },
      ],
      firestoreCollection: "US_TX-annualReportStatusV2Referrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 3,
      ineligibleCriteriaCopy: [
        {
          key: "supervisionLevelIsMinimumFor3Years",
          text: "Less than one month until 3 years on Low",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Less than one month until 2 years with no sustained violation",
        },
      ],
      initialHeader:
        "Review clients who may be eligible for Annual Report Status",
      isAlert: false,
      markSubmittedOnFormDownload: false,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf",
      nonOmsCriteria: [
        {
          text: "The client has demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement, if applicable",
          tooltip:
            "Any supervision, crime victim fees and Post Secondary Education reimbursement which will continue to be due each month, unless paid in advance, while client is on Annual Report status.",
        },
        {
          text: "The client has maintained compliance with all restitution obligations for the preceding two years of supervision",
          tooltip:
            "Note: Restitution obligation does not have to be paid in full to qualify. Restitution will continue to be due each month until fully paid while client is on Annual Report status.\nIn accordance to PD/POP-3.1.6",
        },
      ],
      nonOmsCriteriaHeader: "Requirements to check",
      notifications: [
        {
          body: "ARS/ERS Policy changes went into effect on July 21, 2025. Learn what this means for you: [FAQ](https://docs.google.com/document/d/e/2PACX-1vSZ6cUATsrYOH4x72IbC3EnULM651f2taDj-rXDqVMdMrCQC_OIscuqWV0rslrlZfZeLHDQrO2af7QY/pub)",
          cta: "Acknowledge & Close",
          id: "25976381-adad-4b37-9313-c6168d509092",
          pages: ["caseload"],
          type: "info",
        },
      ],
      omsCriteriaHeader: " Requirements validated by OIMS data",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["ClientProfileDetails", "CaseNotes", "ActionHistory"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: ["usTxEarlyReleaseFromSupervisionV2"],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TX",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "Supervision clients may be eligible for Annual Report Status if they meet certain criteria. The official policy doc can be found [here](https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf) effective Jul 1, 2025. Review clients who may be eligible below.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: false,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: [
            "Eligible Now",
            "Almost Eligible",
            "Submitted for Review",
            "Awaiting Revisions",
            "Approved in OIMS",
            "Marked Ineligible",
          ],
        },
      ],
      tabPrefaceCopy: [],
      tooltipEligibilityText: "Eligible for Annual Report Status",
      urlSection: "AnnualReportStatusV2",
      zeroGrantsTooltip: null,
    },
    usTxEarlyReleaseFromSupervision: {
      callToAction: null,
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "FEES",
          text: "Client has not demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement",
        },
        {
          key: "RESTITUTION",
          text: "Client has not maintained compliance with all restitution obligations in accordance to PD/POP-3.1.6 for the preceding two years of supervision",
        },
        {
          key: "DISCRETION",
          text: "Per the PO’s discretion, it is not in the best interest of society for the client’s reporting status to be modified to Early Release",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Early Release from Supervision",
      dynamicEligibilityText:
        "client[|s] may be eligible for Early Release from Supervision",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTxServedAtLeastHalfOfRemainingSupervisionSentence",
          text: "Has been under supervision for at least one half of the time that remained on their sentence when released to parole or mandatory supervision",
        },
        {
          key: "supervisionLevelIsMinimumOrLimitedFor3Years",
          text: "Has satisfactorily completed three years on Low supervision",
          tooltip: "As determined by the Texas Risk Assessment System (TRAS)",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Has had no warrant issued during the previous two years of the current parole supervision period",
          tooltip:
            "This does not apply to a warrant issued where subsequently an investigation or administrative review did not sustain a violation",
        },
        {
          key: "noSupervisionSustainedViolationWithin2Years",
          text: "Has not committed any violation of rules or conditions of release, as indicated on the release certificate, during the preceding two year period",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TX-earlyReleaseFromSupervisionReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 1,
      ineligibleCriteriaCopy: [
        {
          key: "supervisionLevelIsMinimumOrLimitedFor3Years",
          text: "Less than one month until 3 years on Low",
        },
        {
          key: "usTxServedAtLeastHalfOfRemainingSupervisionSentence",
          text: "Less than one month until served at least half of remaining sentence",
        },
        {
          key: "noSupervisionSustainedViolationWithin2Years",
          text: "Less than one month until 2 years without any sustained violations",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Less than one month until 2 years without any warrants that sustained a violation",
        },
      ],
      initialHeader:
        "Review clients who may be eligible for Early Release from Supervision",
      isAlert: false,
      markSubmittedOnFormDownload: false,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf",
      nonOmsCriteria: [
        {
          text: "The client has demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement, if applicable",
          tooltip:
            "Any supervision, crime victim fees and Post Secondary Education reimbursement will continue to be due each month, unless paid in advance, while client is on Early Release status.",
        },
        {
          text: "The client has maintained compliance with all restitution obligations for the preceding two years",
          tooltip:
            "Note: Restitution obligation does not have to be paid in full to qualify. Restitution will continue to be due each month until fully paid while client is on Early Release status.\n\nIn accordance to PD/POP-3.1.6",
        },
      ],
      nonOmsCriteriaHeader: "Requirements to check",
      notifications: [
        {
          body: "ARS/ERS Policy changes went into effect on July 21, 2025. Learn what this means for you: [FAQ](https://docs.google.com/document/d/e/2PACX-1vSZ6cUATsrYOH4x72IbC3EnULM651f2taDj-rXDqVMdMrCQC_OIscuqWV0rslrlZfZeLHDQrO2af7QY/pub)",
          cta: "Acknowledge & Close",
          id: "4ef11701-0b09-4b81-b5d0-20dcbe6e9d1a",
          pages: ["caseload"],
          title: "",
          type: "info",
        },
      ],
      omsCriteriaHeader: "Requirements validated by OIMS data",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["ClientProfileDetails", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TX",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "Supervision clients may be eligible for Early Release from Supervision if they meet certain criteria. The official policy doc can be found [here](https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf) effective Jul 1, 2025. Review clients who may be eligible below.",
      submittedTabTitle: "Submitted for Review",
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: [],
      tabPrefaceCopy: [],
      tooltipEligibilityText: "Eligible for Early Release from Supervision",
      urlSection: "EarlyReleaseFromSupervision",
      zeroGrantsTooltip: null,
    },
    usTxEarlyReleaseFromSupervisionV2: {
      callToAction: null,
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "FEES",
          text: "Client has not demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement",
        },
        {
          key: "RESTITUTION",
          text: "Client has not maintained compliance with all restitution obligations in accordance to PD/POP-3.1.6 for the preceding two years of supervision",
        },
        {
          key: "DISCRETION",
          text: "Per the PO’s discretion, it is not in the best interest of society for the client’s reporting status to be modified to Early Release",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Early Release from Supervision",
      dynamicEligibilityText:
        "client[|s] may be eligible for Early Release from Supervision",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTxServedAtLeastHalfOfRemainingSupervisionSentence",
          text: "Has been under supervision for at least one half of the time that remained on their sentence when released to parole or mandatory supervision",
        },
        {
          key: "supervisionLevelIsMinimumOrLimitedFor3Years",
          text: "Has satisfactorily completed three years on Low supervision",
          tooltip: "As determined by the Texas Risk Assessment System (TRAS)",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Has had no warrant issued during the previous two years of the current parole supervision period",
          tooltip:
            "This does not apply to a warrant issued where subsequently an investigation or administrative review did not sustain a violation",
        },
        {
          key: "noSupervisionSustainedViolationWithin2Years",
          text: "Has not committed any violation of rules or conditions of release, as indicated on the release certificate, during the preceding two year period",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [
        {
          cellValue: null,
          columnHeader: null,
          columnId: "PERSON_NAME",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "PERSON_DISPLAY_ID",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ELIGIBILITY_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ALMOST_ELIGIBILITY_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SUPERVISION_EXPIRATION_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "STATUS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ALMOST_ELIGIBLE_STATUS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SNOOZE_ENDS_IN",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ASSIGNED_STAFF_NAME",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "INSTANCE_DETAILS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SUBMITTED_FOR",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_CURRENT_REVIEWER",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_SUBMITTED_FOR_REVIEW_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_ALL_REVIEWERS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_REVISION_REASON",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "US_TX_GRANT_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "LAST_VIEWED",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "CTA_BUTTON",
          sortingFn: null,
        },
      ],
      firestoreCollection: "US_TX-earlyReleaseFromSupervisionV2Referrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 4,
      ineligibleCriteriaCopy: [
        {
          key: "supervisionLevelIsMinimumOrLimitedFor3Years",
          text: "Less than one month until 3 years on Low",
        },
        {
          key: "usTxServedAtLeastHalfOfRemainingSupervisionSentence",
          text: "Less than one month until served at least half of remaining sentence",
        },
        {
          key: "noSupervisionSustainedViolationWithin2Years",
          text: "Less than one month until 2 years without any sustained violations",
        },
        {
          key: "usTxNoWarrantWithSustainedViolationWithin2Years",
          text: "Less than one month until 2 years without any warrants that sustained a violation",
        },
      ],
      initialHeader:
        "Review clients who may be eligible for Early Release from Supervision",
      isAlert: false,
      markSubmittedOnFormDownload: false,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf",
      nonOmsCriteria: [
        {
          text: "The client has demonstrated a good faith effort to comply with supervision, crime victim fees and Post Secondary Education reimbursement, if applicable",
          tooltip:
            "Any supervision, crime victim fees and Post Secondary Education reimbursement will continue to be due each month, unless paid in advance, while client is on Early Release status.",
        },
        {
          text: "The client has maintained compliance with all restitution obligations for the preceding two years",
          tooltip:
            "Note: Restitution obligation does not have to be paid in full to qualify. Restitution will continue to be due each month until fully paid while client is on Early Release status.\n\nIn accordance to PD/POP-3.1.6",
        },
      ],
      nonOmsCriteriaHeader: "Requirements to check",
      notifications: [
        {
          body: "ARS/ERS Policy changes went into effect on July 21, 2025. Learn what this means for you: [FAQ](https://docs.google.com/document/d/e/2PACX-1vSZ6cUATsrYOH4x72IbC3EnULM651f2taDj-rXDqVMdMrCQC_OIscuqWV0rslrlZfZeLHDQrO2af7QY/pub)",
          cta: "Acknowledge & Close",
          id: "3388f7ca-b292-4d92-aa87-54a16b9c2948",
          pages: ["caseload"],
          type: "info",
        },
      ],
      omsCriteriaHeader: "Requirements validated by OIMS data",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["ClientProfileDetails", "CaseNotes", "ActionHistory"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TX",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "Supervision clients may be eligible for Early Release from Supervision if they meet certain criteria. The official policy doc can be found [here](https://www.tdcj.texas.gov/documents/pd/03.02.30_parole_policy.pdf) effective Jul 1, 2025. Review clients who may be eligible below.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: false,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: [
            "Eligible Now",
            "Almost Eligible",
            "Submitted for Review",
            "Awaiting Revisions",
            "Approved in OIMS",
            "Marked Ineligible",
          ],
        },
      ],
      tabPrefaceCopy: [],
      tooltipEligibilityText: "Eligible for Early Release from Supervision",
      urlSection: "EarlyReleaseFromSupervisionV2",
      zeroGrantsTooltip: null,
    },
    usTxRemovalFromElectronicMonitoring: {
      callToAction: null,
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "DISCRETION",
          text: "Per the PO’s discretion, it is not in the best interest of society for the client to be removed from Electronic Monitoring.",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Withdrawal from Electronic Monitoring",
      dynamicEligibilityText:
        "client[|s] may be eligible for withdrawal of special condition T and Removal from Electronic Monitoring",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTxElectronicMonitoringForAtLeast60Days",
          text: "Has been on electronic monitoring for at least 60 days.",
        },
        {
          key: "usTxAtLeast30DaysSinceLastRejectionForEmRemoval",
          text: "Has not had a rejection for removal of special condition T in the last 30 days. ",
        },
        {
          key: "atLeast60DaysSinceNegativeDrugTestStreakBegan",
          text: "Has not had positive drug test results for at least 60 days.",
        },
        {
          key: "noSupervisionViolationWithin30Days",
          text: "Has not committed any violation of rules or conditions of release, as indicated on the release certificate, in the last 30 days.",
        },
        {
          key: "usTxAtLeast60DaysSinceFacilityOrTemporaryHousingEnded",
          text: "Has not resided in a residential treatment facility or other correctional facility for at least 60 days.",
        },
        {
          key: "usTxNotInterstateCompactIn",
          text: "Is not a client from another state.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [
        {
          cellValue: null,
          columnHeader: null,
          columnId: "PERSON_NAME",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "PERSON_DISPLAY_ID",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ELIGIBILITY_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ALMOST_ELIGIBILITY_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SUPERVISION_EXPIRATION_DATE",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "STATUS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ALMOST_ELIGIBLE_STATUS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "SNOOZE_ENDS_IN",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "ASSIGNED_STAFF_NAME",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "INSTANCE_DETAILS",
          sortingFn: null,
        },
        {
          cellValue: null,
          columnHeader: null,
          columnId: "LAST_VIEWED",
          sortingFn: null,
        },
      ],
      firestoreCollection: "US_TX-removalFromElectronicMonitoringReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 5,
      ineligibleCriteriaCopy: [
        {
          key: "usTxElectronicMonitoringForAtLeast60Days",
          text: "Less than one month until 60 days on electronic monitoring.",
        },
        {
          key: "usTxAtLeast60DaysSinceFacilityOrTemporaryHousingEnded",
          text: "Less than one month until 60 days out of a residential treatment facility or other correctional facility.",
        },
      ],
      initialHeader:
        "Search above to review and refer people eligible for opportunities like Annual Report Status, Early Release from Supervision, and Removal from Electronic Monitoring.",
      isAlert: false,
      markSubmittedOnFormDownload: false,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://www.tdcj.texas.gov/documents/pd/03.05.01_parole_policy.pdf",
      nonOmsCriteria: [
        { text: "Employment status" },
        { text: "Any pending charges or new arrests" },
        { text: "Status of compliance with special conditions" },
      ],
      nonOmsCriteriaHeader: "Requirements to check",
      notifications: [
        {
          body: "Find the Parole Division's EM policy [here](https://www.tdcj.texas.gov/documents/pd/03.05.01_parole_policy.pdf).",
          cta: "Close",
          id: "757f764b-1710-4300-8f20-d55fbf4d2f91",
          pages: ["caseload"],
          type: "info",
        },
      ],
      omsCriteriaHeader: "Requirements validated by OIMS data",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["ClientProfileDetails", "CaseNotes", "ActionHistory"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TX",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "Clients may be eligible to be submitted to the Board of Pardons and Paroles for withdrawal of special condition  T and Removal of Electronic Monitoring. The official policy doc can be found [here](https://www.tdcj.texas.gov/documents/pd/03.05.01_parole_policy.pdf) effective Sep 1, 2025. Review the below clients to determine if they are eligible.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: false,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: ["Eligible Now", "Almost Eligible", "Marked Ineligible"],
        },
      ],
      tabPrefaceCopy: [],
      tooltipEligibilityText: "Eligible for Withdrawal of Special Condition T",
      urlSection: "RemovalFromElectronicMonitoring",
      zeroGrantsTooltip: null,
    },
  },
} as const satisfies ApiOpportunityConfigurationResponse;
